import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { usePersistentTab } from "../hooks/usePersistentTab";

export type HomeTab = "playlists" | "recent";

export const HOME_TAB_STORAGE_KEY = "kareoke.homeTab";

function isHomeTab(value: string): value is HomeTab {
  return value === "playlists" || value === "recent";
}

export interface HomeLibraryTabsProps {
  playlistsSlot: ReactNode;
  recentSlot: ReactNode;
}

export function HomeLibraryTabs({ playlistsSlot, recentSlot }: HomeLibraryTabsProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = usePersistentTab<HomeTab>(
    HOME_TAB_STORAGE_KEY,
    "recent",
    isHomeTab,
  );

  const tabs: ReadonlyArray<{ id: HomeTab; label: string }> = [
    { id: "recent", label: t("homeLibrary.tabs.recent") },
    { id: "playlists", label: t("homeLibrary.tabs.playlists") },
  ];

  return (
    <section className="space-y-4">
      <div
        role="tablist"
        aria-label={t("homeLibrary.tablistLabel")}
        className="flex flex-wrap gap-2 border-b border-stone-800"
      >
        {tabs.map((tab) => {
          const selected = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`home-library-panel-${tab.id}`}
              id={`home-library-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 ${
                selected
                  ? "border-brand-500 text-brand-300"
                  : "border-transparent text-stone-400 hover:text-stone-100"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`home-library-panel-${activeTab}`}
        aria-labelledby={`home-library-tab-${activeTab}`}
      >
        {activeTab === "playlists" ? playlistsSlot : recentSlot}
      </div>
    </section>
  );
}
